import type { Lens } from '@hookform/lenses';
import cn from 'classnames';
import { useController } from 'react-hook-form';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type {
  AddIdentityProviderBaseFormData,
  EditIdentityProviderBaseFormData,
} from './IdentityProviderBaseForm.schema.ts';
import styles from './IdentityProviderBaseForm.module.css';

type AddProps = {
  lens: Lens<AddIdentityProviderBaseFormData>;
  isEditing: false;
  className?: string;
};
type EditProps = {
  lens: Lens<EditIdentityProviderBaseFormData>;
  isEditing: true;
  className?: string;
};

export function IdentityProviderBaseForm({ lens, isEditing, className }: AddProps | EditProps) {
  const { field: clientIDField, fieldState: clientIDState } = useController(lens.focus('clientID').interop());
  const { field: clientSecretField, fieldState: clientSecretState } = useController(
    lens.focus('clientSecret').interop()
  );
  const { field: issuerURLField, fieldState: issuerURLState } = useController(lens.focus('issuerURL').interop());

  return (
    <Spacer direction="vertical" className={className}>
      <Spacer size="wide" stackWhenNarrow={true}>
        <Field label="Client ID" className="width-100">
          <Input className="width-100" maxLength={256} hasError={Boolean(clientIDState.error)} {...clientIDField} />
          {clientIDState.error?.message ?
            <ErrorMessage className="mb-8">{clientIDState.error.message}</ErrorMessage>
          : null}
        </Field>
        <Field label="Client Secret" className="width-100">
          <Input
            type="password"
            autoComplete="new-password"
            className={cn('width-100', {
              [styles.IdentityProviderBaseForm_clientSecret___exists]: isEditing,
            })}
            maxLength={256}
            hasError={Boolean(clientSecretState.error)}
            {...clientSecretField}
            placeholder={isEditing ? '••••••••••••••••••••••••' : undefined}
          />
          {clientSecretState.error?.message ?
            <ErrorMessage className="mb-8">{clientSecretState.error.message}</ErrorMessage>
          : null}
        </Field>
      </Spacer>

      <Field label="Issuer URL">
        <Input className="width-100" maxLength={256} hasError={Boolean(issuerURLState.error)} {...issuerURLField} />
        {issuerURLState.error?.message ?
          <ErrorMessage className="mb-8">{issuerURLState.error.message}</ErrorMessage>
        : null}
      </Field>
    </Spacer>
  );
}
